import { useCompteursBoite } from '../../api/boite-reception.js';
import { RejeuScans } from '../inventaire/RejeuScans.js';
import { useConsultationHorsLigne } from '../../horsLigne/index.js';
import { BandeauMiseAJour } from '../racine/BandeauMiseAJour.js';
import { BandeauDoubleAuthentification } from './BandeauDoubleAuthentification.js';
import { modeDemonstrationActif } from '../../api/demo.js';
import { useSynchroHorsLigne } from '../../horsLigne/synchro.js';
import type { JSX } from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onlineManager, useQuery } from '@tanstack/react-query';
import { lireSessionVerifiee } from '../../auth/session.js';
import { CLE_SESSION } from '../../auth/queryClient.js';
import { surCompteChange } from '../../auth/identite.js';
import { surConfinement } from '../../auth/confinement.js';
import type { SessionUtilisateur } from '../../api/types.js';
import {
  Navigate,
  Outlet,
  useBlocker,
  useLoaderData,
  useLocation,
  useMatch,
  useNavigate,
  useRevalidator,
  type LoaderFunctionArgs,
} from 'react-router';
import { definitionModule, moduleDeLaNavigation, type Module } from '@artosera/shared';
import { chargerCoquille } from '../loaders.js';
import { BandeauHorsLigne } from '../racine/BandeauHorsLigne.js';
import { consommerRetourLocal, memoriserPageAffichee } from '../racine/horsLigne.js';
import { useAuthentification } from '../../auth/useAuthentification.js';
import {
  galerieALaction,
  galerieAModule,
  galerieVoitModule,
  modulesLectureSeuleDeLaSession,
  moduleEnLectureSeule,
  sujetDeLaGalerie,
  sujetGroupeDuCompte,
} from '../../auth/sujet.js';
import { definirModulesLectureSeule, galerieDeLaPage } from '../../auth/lectureSeule.js';
import { ModuleNonActivePage } from '../module/ModuleNonActivePage.js';
import { LectureSeuleProvider, PageEnLectureSeule } from '../module/LectureSeule.js';
import { modulesAVenirDe } from '../modules/modulesAVenir.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { trouverGalerie } from '../../auth/session.js';
import { entreesVisibles } from './navigation.js';
import { Sidebar } from './Sidebar.js';
import { TopBar } from './TopBar.js';
import { MobileNav } from './MobileNav.js';
import { CommandPalette } from './CommandPalette.js';
import { Footer } from './Footer.js';
import { useNavReduite } from './hooks.js';
import { useDesign } from '../../appearance/useDesign.js';
import {
  definirPolitiqueTheme,
  POLITIQUE_LIBRE,
  politiqueTheme,
} from '../../appearance/politique.js';
import { useEntryParams } from '../../preferences/useEntryParams.js';
import { useUserLanguage } from '../../preferences/useUserLanguage.js';
import { HorizontalNav } from './HorizontalNav.js';
import { useAutoFairLayout } from './useAutoFairLayout.js';
import styles from './RegieShell.module.css';

export async function coquilleLoader({ request, params }: LoaderFunctionArgs) {
  // Retour local demandé depuis la page d'erreur (constat F) : voir `horsLigne.ts`.
  const retour = consommerRetourLocal(request.url);
  if (retour) return retour;
  return chargerCoquille(request.url, params['slug']);
}

/**
 * Coquille de la régie (`/:slug/*`, 3.2) : barre latérale, barre supérieure,
 * menu mobile, palette ⌘K, mode foire, pied de page. Les pages de module
 * lisent la galerie et le sujet courants par `useShellContext()`.
 */
export function RegieShell(): JSX.Element | null {
  const surLeStand = useMatch({ path: '/:galerie/stand', end: true }) !== null;
  const donneesCoquille = useLoaderData<typeof coquilleLoader>();
  const { galerieSlug } = donneesCoquille;
  // Source UNIQUE de la session affichée : la requête de session (relecture
  // du 23/09, constat G). Le chargeur la remplit ; la revalidation du stand
  // la remplace — des droits révoqués disparaissent aussitôt de la coquille
  // (navigation, actions, pages). Vidée (déconnexion), la coquille n'affiche
  // plus rien.
  const { data: session } = useQuery<SessionUtilisateur>({
    queryKey: CLE_SESSION,
    queryFn: lireSessionVerifiee,
    enabled: false,
    staleTime: Infinity,
  });
  const { deconnecter } = useAuthentification();
  const galerie = session ? trouverGalerie(session, galerieSlug) : null;
  const compteursBoite = useCompteursBoite(
    galerieSlug,
    Boolean(
      galerie &&
      galerieVoitModule(galerie, 'boite-reception') &&
      galerieALaction(galerie, 'inbox:read'),
    ),
  );
  // Lot C : le thème de la galerie, réglé dans la console, et le choix laissé
  // (ou non) à ses utilisateurs. Publié pour toute la régie en effet de mise
  // en page : le rendu qui suit est synchrone, avant que le navigateur peigne.
  const politique = politiqueTheme(galerie);
  useLayoutEffect(() => {
    definirPolitiqueTheme({ theme: politique.theme, libre: politique.libre });
  }, [politique.theme, politique.libre]);
  useLayoutEffect(() => () => definirPolitiqueTheme(POLITIQUE_LIBRE), []);
  const { horizontal } = useDesign(session?.compte.id ?? null);
  useEntryParams(session?.compte.id ?? null, politique.libre);
  useUserLanguage(session?.compte.id ?? null, session?.compte.langue ?? 'fr');
  const { t } = useTranslation('regie');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Le module de la page, lu dans le même registre que la navigation et la
  // garde de l'API. Sous un module en lecture seule, la page voit une galerie
  // sans écriture (`galerieDeLaPage`) ; les modules en lecture seule sont aussi
  // posés pour que la démonstration puisse refuser leurs écritures comme l'API.
  const moduleDemande = moduleDeLaNavigation(pathname.slice(`/${galerieSlug}`.length));
  const galeriePage = useMemo(
    () => (galerie ? galerieDeLaPage(galerie, moduleDemande) : null),
    [galerie, moduleDemande],
  );
  const lectureSeuleDeLaGalerie = galerie ? modulesLectureSeuleDeLaSession(galerie) : [];
  const cleLectureSeule = lectureSeuleDeLaGalerie.join(',');
  useLayoutEffect(() => {
    definirModulesLectureSeule(
      cleLectureSeule === '' ? [] : (cleLectureSeule.split(',') as Module[]),
    );
    return () => definirModulesLectureSeule([]);
  }, [cleLectureSeule]);

  const [reduite, setReduite] = useNavReduite();
  const [menuMobileOuvert, setMenuMobileOuvert] = useState(false);
  const [paletteOuverte, setPaletteOuverte] = useState(false);

  useAutoFairLayout();
  useSynchroHorsLigne(
    galerieSlug,
    !session?.consultationHorsLigne &&
      !modeDemonstrationActif() &&
      Boolean(session?.galeries.find((g) => g.slug === galerieSlug)?.horsLigne),
  );

  // `409 compte_change` (constat N3) : le cookie n'est plus ce compte. Ses
  // requêtes sont déjà purgées ; la route est revalidée, et la session
  // fraîche fixe la nouvelle identité (avec purge si le compte a changé).
  const revalidateur = useRevalidator();
  const consultation = useConsultationHorsLigne();
  useEffect(() => {
    if (!consultation) return;
    // À froid, TanStack peut encore annoncer online=true sans avoir reçu
    // d'événement offline. Écouter le navigateur garantit le retour réel.
    const auRetour = () => {
      void revalidateur.revalidate();
    };
    window.addEventListener('online', auRetour);
    return () => window.removeEventListener('online', auRetour);
  }, [consultation, revalidateur]);
  useEffect(
    () =>
      surCompteChange(() => {
        void revalidateur.revalidate();
      }),
    [revalidateur],
  );
  // Refus pour confinement en cours d'usage (CGU, double authentification) :
  // même chemin. Le chargeur relit la session et mène à la page qui la
  // libère, avec le retour.
  useEffect(
    () =>
      surConfinement(() => {
        void revalidateur.revalidate();
      }),
    [revalidateur],
  );

  // La dernière page réellement affichée : la sortie locale de la page
  // d'erreur hors ligne (constat F).
  const adresse = useLocation();
  useEffect(() => {
    memoriserPageAffichee(`${adresse.pathname}${adresse.search}`, donneesCoquille);
  }, [adresse.pathname, adresse.search, donneesCoquille]);

  // Hors ligne, changer de galerie (ou revenir à la racine) exigerait une
  // session fraîche que le réseau ne peut pas donner : la navigation est
  // bloquée et le bandeau le dit (relecture du 23/09, constat 6). Le reste —
  // pages de la galerie déjà chargées, déconnexion — passe.
  const [galerieRefusee, setGalerieRefusee] = useState(false);
  const blocage = useBlocker(({ currentLocation, nextLocation }) => {
    if (onlineManager.isOnline()) return false;
    const [, avant = ''] = currentLocation.pathname.split('/');
    const [, apres = ''] = nextLocation.pathname.split('/');
    if (apres === avant) return false;
    const refusee =
      apres === '' || (session?.galeries ?? []).some((candidate) => candidate.slug === apres);
    if (refusee) setGalerieRefusee(true);
    return refusee;
  });
  useEffect(() => {
    if (blocage.state === 'blocked') blocage.reset();
  }, [blocage]);
  useEffect(() => onlineManager.subscribe((enLigne) => enLigne && setGalerieRefusee(false)), []);

  useEffect(() => {
    function surRaccourci(event: KeyboardEvent): void {
      const cible = event.target as HTMLElement | null;
      const dansChamp =
        cible?.tagName === 'INPUT' || cible?.tagName === 'TEXTAREA' || cible?.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOuverte((valeur) => !valeur);
        return;
      }
      // Droits visibles (ARTO-53, § 4) : le raccourci N n'ouvre la création
      // que pour qui a `artworks:write` — le comptable n'a pas « Nouvelle
      // œuvre » (audit du 23 septembre 2026, § 1).
      if (
        !dansChamp &&
        event.key.toLowerCase() === 'n' &&
        galerie &&
        galerieALaction(galerie, 'artworks:write')
      ) {
        event.preventDefault();
        navigate(`/${galerieSlug}/oeuvres?nouvelle=1`);
      }
    }
    document.addEventListener('keydown', surRaccourci);
    return () => document.removeEventListener('keydown', surRaccourci);
  }, [galerie, galerieSlug, navigate]);

  if (!session) return null;

  if (!galerie) {
    return (
      <div className={styles.sansGalerie}>
        <p>{t('coquille.sansGalerie')}</p>
      </div>
    );
  }

  const sujet = sujetDeLaGalerie(galerie);
  const sujetGroupe = sujetGroupeDuCompte(session.compte, galerie);

  // Module non activé (D21) : une URL directe vers un module complémentaire
  // que la galerie n'a pas ne monte AUCUN écran du module — donc aucun appel
  // à l'API, qui refuserait de toute façon. Un module en lecture seule, lui,
  // s'ouvre : ses lectures répondent, ses écritures sont retirées de la page.
  const moduleInactif =
    moduleDemande !== null && !galerieVoitModule(galerie, moduleDemande) ? moduleDemande : null;
  const moduleLectureSeule =
    moduleDemande !== null && moduleEnLectureSeule(galerie, moduleDemande) ? moduleDemande : null;
  // Module À VENIR (palier verrouillé pour le parc) : l'URL directe de son écran
  // mène à sa fiche statique, jamais à la page « Module non activé » ni à l'écran.
  const avenir = modulesAVenirDe(session, galerie);
  const moduleAVenirDemande =
    moduleInactif !== null && (avenir.modules as readonly Module[]).includes(moduleInactif)
      ? moduleInactif
      : null;

  const pageCourante = (
    <Outlet
      context={{
        session,
        compte: session.compte,
        galerie: galeriePage ?? galerie,
        galerieSlug,
        sujet,
      }}
    />
  );

  return (
    <div
      className={styles.coquille}
      data-testid="regie-shell"
      data-ui="regie-shell"
      data-layout={horizontal ? 'horizontal' : 'sidebar'}
    >
      {/* Lien d'évitement : premier arrêt au clavier, il saute la navigation
          et la barre supérieure pour aller droit au contenu. */}
      <a
        className={styles.evitement}
        data-evitement
        href="#main"
        onClick={(event) => {
          event.preventDefault();
          const contenu = document.getElementById('main');
          contenu?.focus();
          contenu?.scrollIntoView({ block: 'start' });
        }}
      >
        {t('coquille.allerAuContenu')}
      </a>
      {!horizontal ? (
        <div className={styles.desktopNav}>
          <Sidebar
            galerie={galerie}
            galerieSlug={galerieSlug}
            sujetGroupe={sujetGroupe}
            modulesAVenir={avenir.modules.length}
            nonLues={compteursBoite.data?.nonLues ?? 0}
            reduite={reduite}
            onReduireChange={setReduite}
          />
        </div>
      ) : null}

      <MobileNav
        open={menuMobileOuvert}
        onClose={() => setMenuMobileOuvert(false)}
        galerie={galerie}
        galerieSlug={galerieSlug}
        sujetGroupe={sujetGroupe}
        modulesAVenir={avenir.modules.length}
        nonLues={compteursBoite.data?.nonLues ?? 0}
      />

      <div className={styles.colonne} data-ui="shell-column">
        {horizontal ? (
          <HorizontalNav
            galerie={galerie}
            galerieSlug={galerieSlug}
            sujetGroupe={sujetGroupe}
            modulesAVenir={avenir.modules.length}
            nonLues={compteursBoite.data?.nonLues ?? 0}
          />
        ) : null}
        <TopBar
          compte={session.compte}
          galerie={galerie}
          galeries={session.galeries}
          galerieSlug={galerieSlug}
          langue={session.compte.langue}
          onOuvrirRecherche={() => setPaletteOuverte(true)}
          onOuvrirMenuMobile={() => setMenuMobileOuvert(true)}
          onDeconnexion={() => void deconnecter()}
        />

        <main id="main" tabIndex={-1} className={styles.contenu} data-ui="main">
          <BandeauMiseAJour />
          <BandeauDoubleAuthentification session={session} galerieSlug={galerieSlug} />
          {galerieAModule(galerie, 'reserve-inventaire') &&
            galerieALaction(galerie, 'inventaire:write') && (
              <RejeuScans
                key={`${session.compte.id}:${galerieSlug}`}
                compteId={session.compte.id}
                galerie={galerieSlug}
              />
            )}
          {surLeStand && !consultation ? null : (
            <BandeauHorsLigne galerieRefusee={galerieRefusee} consultation={consultation} />
          )}
          {moduleAVenirDemande !== null ? (
            <Navigate replace to={`/${galerieSlug}/modules/${moduleAVenirDemande}`} />
          ) : moduleInactif !== null && definitionModule(moduleInactif).famille === 'addon' ? (
            // Un add-on inactif ne se nomme pas (S0 § 5) : une galerie cliente
            // ne doit jamais lire le nom d'un add-on non publié. La page est
            // introuvable, comme pour une URL inconnue.
            <NotFoundPage />
          ) : moduleInactif !== null ? (
            <ModuleNonActivePage
              module={moduleInactif}
              galerieSlug={galerieSlug}
              lienArchive={galerieALaction(galerie, 'invoices:read')}
            />
          ) : (
            <LectureSeuleProvider
              valeur={{
                module: moduleLectureSeule,
                galerieReelle: galerie,
                galeriePage: galeriePage ?? galerie,
              }}
            >
              {moduleLectureSeule !== null ? (
                <PageEnLectureSeule>{pageCourante}</PageEnLectureSeule>
              ) : (
                pageCourante
              )}
            </LectureSeuleProvider>
          )}
        </main>

        <Footer />
      </div>

      <CommandPalette
        open={paletteOuverte}
        onOpenChange={setPaletteOuverte}
        entrees={entreesVisibles(galerie)}
        galerie={galerie}
        galerieSlug={galerieSlug}
        demander={
          galerieAModule(galerie, 'assistant-recherche') &&
          galerieALaction(galerie, 'recherche:ask')
        }
      />
    </div>
  );
}
